import {
    DataDictionaryIcon,
    DbSchemaIcon,
    SmartContractIcon,
    UploadFileIcon,
    TruncateJobIcon,
    DownloadJobIcon,
    ResetSDZDataIcon,
    WipeSDZDataIcon,
    DropChainJobIcon,
    ExportDataJobIcon,
} from '../../assets';
import { JOB_MANAGER_PATHS } from 'constants/job-manager-paths';
import {
    CreateAccordionMenuItemsData,
    CreateFeatureMenuItem,
    FeatureItems,
} from '../../components/create-feature-dialog/create-feature-dialog-helper';
import {
    defaultQueryPropsModel,
    JobSummary,
    QueryPropsModel,
    themeObject,
} from 'lib';
import { downloadFile } from '../../pages/export/export-helper';
import * as yup from 'yup';

export interface TotalJobSummary {
    list: JobSummary[];
    total: number;
}

export type JobManagerQueryPropsModel = QueryPropsModel & {
    days: number;
};

export const defaultJobManagerQueryPropsModel: JobManagerQueryPropsModel = {
    ...defaultQueryPropsModel,
    days: 5,
};

export const defaultJobSummary: TotalJobSummary = {
    list: [],
    total: 0,
};

export const noOfDays: {
    label: string;
    value: number;
}[] = [
    {
        label: 'Last 5 Days',
        value: 5,
    },
    {
        label: 'Last 15 Days',
        value: 15,
    },
    {
        label: 'Last 30 Days',
        value: 30,
    },
    {
        label: 'Last 60 Days',
        value: 60,
    },
];

export const selectFieldStyles = {
    width: '220px',
    mr: '30px',
};

export const fileTypes = [
    'CSV',
    'EDI',
    'JSON',
    'PARQUET',
    'PIPE',
    'SPSS',
    'TAB',
    'XLS',
    'XLSX',
    'XLSM',
    'XML',
];

export const downloadFileTypes = ['CSV', 'JSON', 'XLSX'];

export const JOB_MANAGER_FEATURE_ITEMS: FeatureItems = {
    UPLOAD_FILE: {
        title: 'Upload Data',
        subtitle: 'Upload data using a file.',
        icon: UploadFileIcon,
        navigationPath: JOB_MANAGER_PATHS.CREATE_UPLOAD_FILE_JOB,
    },
    DBSCHEMA: {
        title: 'Import DbSchema',
        subtitle: 'Upload a DbSchema file to update the data model.',
        icon: DbSchemaIcon,
        navigationPath: JOB_MANAGER_PATHS.CREATE_DBSCHEMA_JOB,
    },
    DATA_DICTIONARY: {
        title: 'Import Dictionary',
        subtitle: 'Upload a JSON dictionary file.',
        icon: DataDictionaryIcon,
        navigationPath: JOB_MANAGER_PATHS.CREATE_UPLOAD_DICTIONARY_JOB,
    },
    DOWNLOAD_JOB: {
        title: 'Download Data',
        subtitle: 'Download data from a TQL query as a file.',
        icon: DownloadJobIcon,
        navigationPath: JOB_MANAGER_PATHS.CREATE_DOWNLOAD_JOB,
    },
    TRUNCATE_CHAIN: {
        title: 'Truncate a Chain',
        subtitle: 'Remove all data from a chain.',
        icon: TruncateJobIcon,
        navigationPath: JOB_MANAGER_PATHS.CREATE_TRUNCATE_CHAIN_JOB,
    },
    EXECUTE_SMART_CONTRACT: {
        title: 'Execute Smart Contract',
        subtitle: 'Execute a smart contract with optional parameters.',
        icon: SmartContractIcon,
        navigationPath: JOB_MANAGER_PATHS.CREATE_EXECUTE_SMART_CONTRACT_JOB,
    },
    WIPE_SDZ_DATA: {
        title: 'Wipe SDZ Data',
        subtitle:
            'Removes all data, history, contracts, and jobs while preserving metadata.',
        icon: WipeSDZDataIcon,
        navigationPath: JOB_MANAGER_PATHS.WIPE_SDZ_DATA,
        styles: {
            width: '70.03px',
            height: '64.62px',
        },
    },
    RESET_SDZ_DATA: {
        title: 'Reset SDZ',
        subtitle:
            'Clears all data and metadata, restoring the SDZ to its initial state.',
        icon: ResetSDZDataIcon,
        navigationPath: JOB_MANAGER_PATHS.RESET_SDZ_DATA,
        styles: {
            width: '67px',
            height: '60.43px',
            marginRight: '10px',
        },
    },
    DROP_CHAIN: {
        title: 'Drop a Chain',
        subtitle: 'Delete a chain from metadata and all data.',
        icon: DropChainJobIcon,
        navigationPath: JOB_MANAGER_PATHS.DROP_CHAIN,
        styles: {
            width: '75px',
            height: '66.02px',
            marginRight: '-8px',
        },
    },
    EXPORT_DATA: {
        title: 'Export Data',
        subtitle: 'Export a full chain in JSON for archiving.',
        icon: ExportDataJobIcon,
        navigationPath: JOB_MANAGER_PATHS.EXPORT_DATA,
        styles: {
            width: '70px',
            height: '62.35px',
        },
    },
};

export const dataPurgeJobsMenuItems: CreateFeatureMenuItem[] = [
    JOB_MANAGER_FEATURE_ITEMS.TRUNCATE_CHAIN,
    JOB_MANAGER_FEATURE_ITEMS.DROP_CHAIN,
    JOB_MANAGER_FEATURE_ITEMS.WIPE_SDZ_DATA,
    JOB_MANAGER_FEATURE_ITEMS.RESET_SDZ_DATA,
];

export const metadataJobsMenuItems: CreateFeatureMenuItem[] = [
    JOB_MANAGER_FEATURE_ITEMS.DBSCHEMA,
    JOB_MANAGER_FEATURE_ITEMS.DATA_DICTIONARY,
];

export const dataJobsMenuItems: CreateFeatureMenuItem[] = [
    JOB_MANAGER_FEATURE_ITEMS.UPLOAD_FILE,
    JOB_MANAGER_FEATURE_ITEMS.DOWNLOAD_JOB,
    JOB_MANAGER_FEATURE_ITEMS.EXPORT_DATA,
    JOB_MANAGER_FEATURE_ITEMS.EXECUTE_SMART_CONTRACT,
];

export const getFeatureMenuItemsByRoles = (
    isUserRole: boolean,
    isAdminRole: boolean
): CreateAccordionMenuItemsData[] => {
    let featureMenuItems: CreateAccordionMenuItemsData[] = [];
    if (isUserRole) {
        featureMenuItems = [
            {
                heading: 'Data Jobs',
                menuItems: dataJobsMenuItems,
                isExpanded: false,
            },
        ];
    }
    if (isAdminRole) {
        featureMenuItems = [
            {
                heading: 'Metadata Jobs',
                menuItems: metadataJobsMenuItems,
                isExpanded: false,
            },
            {
                heading: 'Data Purge Jobs',
                menuItems: dataPurgeJobsMenuItems,
                isExpanded: false,
            },
        ];
    }
    if (isUserRole && isAdminRole) {
        featureMenuItems = [
            {
                heading: 'Metadata Jobs',
                menuItems: metadataJobsMenuItems,
                isExpanded: false,
            },
            {
                heading: 'Data Jobs',
                menuItems: dataJobsMenuItems,
                isExpanded: false,
            },
            {
                heading: 'Data Purge Jobs',
                menuItems: dataPurgeJobsMenuItems,
                isExpanded: false,
            },
        ];
    }
    return featureMenuItems;
};

export const FILE_UPLOAD_SUCCESS_MSG =
    'Your job has started successfully. You can see the status of your job on the Job Manager page.';

export const downloadData = (response: any): void => {
    const nameMatches =
        response.headers['content-disposition'].match(/filename="([^"]+)"/);

    // get the default name from the header's content disposition. If for some reason it's missing, set a default name.
    const filename = nameMatches.length >= 2 ? nameMatches[1] : 'download_file';

    if (filename.endsWith('.json')) {
        downloadFile(
            response.data,
            filename,
            'application/json;charset=utf-8;'
        );
    } else if (filename.endsWith('.csv')) {
        downloadFile(response.data, filename, 'text/csv');
    } else {
        // For any other file types set it to generic contentType and let it download base on the filename.
        downloadFile(response.data, filename, 'application/octet-stream');
    }
};

export const wipeOrResetSdzDataJobStyle = {
    container: {
        display: 'flex',
        justifyContent: 'center',
        mt: 1.5,
    },
    customButtonContainerStyles: {
        justifyContent: 'center',
    },
    textStyles: {
        textAlign: 'center',
        lineHeight: '21.17px',
        color: themeObject.palette.grayScale[800],
    },
};
export interface TruncateOrDropChainJobFormInputs {
    chain: string;
}

export const TruncateOrDropChainJobSchema = yup.object({
    chain: yup.string().required(),
});

export const truncateOrDropChainJobDefaults = {
    chain: '',
};
